import { useState } from "react";
import {
    Box,
    VStack,
    Text,
    Input,
    Button,
    Image,
    InputGroup,
} from "@chakra-ui/react";
import {
    useDispatch,
    useSelector,
} from "react-redux";
import {
    Eye,
    EyeOff,
} from "lucide-react";
import {
    useNavigate,
} from "react-router-dom";

import {
    registerAction,
} from "../../../../state/actions/authActions";

import {
    toaster,
} from "../../../../components/toaster";

import ActivityToast from "../../../../components/ActivityToast";
import AppCheckbox from "../../../../components/AppCheckBox";

import routes from "../../../../routes";
import Logo from "../../../../assets/logo.png";

const RegisterForm = () => {
    const dispatch =
        useDispatch();

    const navigate =
        useNavigate();

    const {
        loading,
    } = useSelector(
        (state) =>
            state.auth
    );

    const [
        form,
        setForm,
    ] = useState({
        username: "",
        email: "",
        password: "",
        phone_number: "",
    });

    const [
        showPassword,
        setShowPassword,
    ] = useState(false);

    const [
        agreement,
        setAgreement,
    ] = useState(false);

    const handleChange = (
        field,
        value
    ) => {
        setForm(
            (prev) => ({
                ...prev,
                [field]:
                    value,
            })
        );
    };

    const isFormValid =
        form.username.trim() &&
        form.email.trim() &&
        form.password.trim() &&
        form.phone_number.trim();

    const handleSubmit =
        async () => {
            const payload =
                {
                    username:
                        form.username,
                    email:
                        form.email,
                    password:
                        form.password,
                    phone_number: `+267${form.phone_number}`,
                    user_type:
                        "Driver Partner",
                    agreement_accepted:
                        true,
                };

            const result =
                await dispatch(
                    registerAction(
                        payload
                    )
                );

            if (
                result?.success
            ) {
                toaster.create(
                    {
                        render:
                            () => (
                                <ActivityToast
                                    type="success"
                                    title="Account Created"
                                    description="We've sent an OTP to your email address."
                                    timestamp="Just now"
                                />
                            ),
                    }
                );

                setTimeout(
                    () => {
                        navigate(
                            routes.verifyOtp,
                            {
                                state:
                                    {
                                        user_id:
                                            result
                                                .data
                                                ?.data
                                                ?.user_id,
                                        email:
                                            payload.email,
                                    },
                            }
                        );
                    },
                    1000
                );
            } else {
                toaster.create(
                    {
                        render:
                            () => (
                                <ActivityToast
                                    type="error"
                                    title="Registration Failed"
                                    description={
                                        result.error ||
                                        "Unable to create account."
                                    }
                                    timestamp="Just now"
                                />
                            ),
                    }
                );
            }
        };

    return (
        <Box
            p={{
                base: 6,
                md: 10,
                lg: 20,
            }}
        >
            <Box
                textAlign="center"
                mb={10}
            >
                <Image
                    src={Logo}
                    h="52px"
                    mx="auto"
                    mb={12}
                />

                <Text
                    fontSize={{
                        base: "36px",
                        md: "48px",
                    }}
                    fontWeight="700"
                    color="#071D54"
                    mb={3}
                >
                    Become a Driver
                    Partner
                </Text>

                <Text
                    color="gray.600"
                    maxW="420px"
                    mx="auto"
                >
                    Create your
                    account to start
                    referring
                    drivers,
                    tracking
                    milestones,
                    and earning
                    rewards.
                </Text>
            </Box>

            <VStack gap={5}>
                <Input
                    size="lg"
                    placeholder="Username"
                    value={
                        form.username
                    }
                    onChange={(
                        e
                    ) =>
                        handleChange(
                            "username",
                            e.target
                                .value
                        )
                    }
                />

                <Input
                    size="lg"
                    type="email"
                    placeholder="Email address"
                    value={
                        form.email
                    }
                    onChange={(
                        e
                    ) =>
                        handleChange(
                            "email",
                            e.target
                                .value
                        )
                    }
                />

                <InputGroup>
                    <Input
                        size="lg"
                        placeholder="Phone number"
                        value={
                            form.phone_number
                        }
                        onChange={(
                            e
                        ) =>
                            handleChange(
                                "phone_number",
                                e.target.value.replace(
                                    /\D/g,
                                    ""
                                )
                            )
                        }
                        maxLength={
                            8
                        }
                    />
                </InputGroup>

                <InputGroup
                    endElement={
                        showPassword ? (
                            <EyeOff
                                size={
                                    18
                                }
                                cursor="pointer"
                                onClick={() =>
                                    setShowPassword(
                                        false
                                    )
                                }
                            />
                        ) : (
                            <Eye
                                size={
                                    18
                                }
                                cursor="pointer"
                                onClick={() =>
                                    setShowPassword(
                                        true
                                    )
                                }
                            />
                        )
                    }
                >
                    <Input
                        size="lg"
                        placeholder="Password"
                        type={
                            showPassword
                                ? "text"
                                : "password"
                        }
                        value={
                            form.password
                        }
                        onChange={(
                            e
                        ) =>
                            handleChange(
                                "password",
                                e.target
                                    .value
                            )
                        }
                    />
                </InputGroup>

                <AppCheckbox
                    checked={
                        agreement
                    }
                    onCheckedChange={(
                        e
                    ) =>
                        setAgreement(
                            e.checked
                        )
                    }
                >
                    I agree to
                    the Mogoma
                    Partner
                    Terms &
                    Conditions
                    and Privacy
                    Policy.
                </AppCheckbox>

                <Button
                    w="100%"
                    size="lg"
                    bg="#2453FF"
                    color="white"
                    _hover={{
                        bg: "#1E46D8",
                    }}
                    disabled={
                        !isFormValid ||
                        !agreement ||
                        loading
                    }
                    loading={
                        loading
                    }
                    loadingText="Creating Account..."
                    opacity={
                        !isFormValid ||
                        !agreement
                            ? 0.6
                            : 1
                    }
                    onClick={
                        handleSubmit
                    }
                >
                    Create
                    Account
                </Button>
            </VStack>

            <Text
                mt={10}
                textAlign="center"
                color="gray.500"
                fontSize="sm"
            >
                Already have
                an account?{" "}
                <Text
                    as="span"
                    color="#2453FF"
                    fontWeight="600"
                    cursor="pointer"
                    onClick={() =>
                        navigate(
                            routes.login
                        )
                    }
                    _hover={{
                        textDecoration:
                            "underline",
                    }}
                >
                    Sign In
                </Text>
            </Text>
        </Box>
    );
};
export default RegisterForm;